import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { apiFetch } from '../lib/api'
import { formatDateCR } from '../lib/dateUtils'
import LucvanHeader from '../components/LucvanHeader'
import ProductLabel from '../components/ProductLabel'

export default function ProductionTicketDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { currentUser, logout } = useAuth()
  const [ticket, setTicket] = useState(null)
  const [workshopNotes, setWorkshopNotes] = useState([])
  const [newNote, setNewNote] = useState('')
  const [newNoteFiles, setNewNoteFiles] = useState([])
  const [newStatus, setNewStatus] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showLabel, setShowLabel] = useState(false)

  const normalizeStatus = (status) => {
    if (!status) return ''
    const value = status.toString().trim().toLowerCase()

    if (['ready', 'lista para entregar', 'listo para entregar', 'ready_to_deliver'].includes(value)) return 'ready'
    if (['delivered', 'entregado', 'entregada', 'entregados', 'entregadas'].includes(value)) return 'delivered'
    if (['in_production', 'en produccion', 'en producción'].includes(value)) return 'in_production'
    if (['pending', 'pendiente'].includes(value)) return 'pending'

    return status
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  useEffect(() => {
    loadTicketData()
  }, [id])

  async function loadTicketData() {
    try {
      setLoading(true)
      
      // Cargar datos del ticket
      const ticketRes = await apiFetch(`/api/requests/${id}`)
      if (!ticketRes.ok) throw new Error('Error cargando ticket')
      let ticketData = await ticketRes.json()
      
      // Parsear specs si viene como string JSON
      if (ticketData.specs && typeof ticketData.specs === 'string') {
        try {
          ticketData.specs = JSON.parse(ticketData.specs)
        } catch (e) {
          console.warn('Error parsing specs:', e)
          ticketData.specs = {}
        }
      }
      
      setTicket(ticketData)
      setNewStatus(normalizeStatus(ticketData.status) || 'pending')
      
      // Cargar notas del taller
      const notesRes = await apiFetch(`/api/requests/${id}/workshop-notes`)
      if (notesRes.ok) {
        const notesData = await notesRes.json()
        console.log('[ProductionTicketDetail] Respuesta notas (raw):', notesData)
        
        // Manejar tanto arrays como objetos
        let notesArray = []
        if (Array.isArray(notesData)) {
          notesArray = notesData
        } else if (notesData && typeof notesData === 'object') {
          // Si es un objeto, podría tener las notas en una propiedad
          if (Array.isArray(notesData.notes)) {
            notesArray = notesData.notes
          } else if (Array.isArray(notesData.data)) {
            notesArray = notesData.data
          } else if (Array.isArray(notesData.workshop_notes)) {
            notesArray = notesData.workshop_notes
          } else {
            // Convertir el objeto a array de valores
            const values = Object.values(notesData).filter(v => typeof v === 'object')
            notesArray = values.length > 0 ? values : []
          }
        }
        
        console.log('[ProductionTicketDetail] Notas cargadas:', notesArray)
        setWorkshopNotes(notesArray)
      }
    } catch (err) {
      setError('Error cargando el ticket')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  async function handleAddNote() {
    if (!newNote.trim()) return

    if (newNoteFiles.length > 4) {
      alert('Solo puedes adjuntar hasta 4 archivos por nota')
      return
    }
    
    try {
      const formData = new FormData()
      formData.append('comment', newNote)
      newNoteFiles.forEach((file) => formData.append('files', file))

      const res = await apiFetch(`/api/requests/${id}/workshop-notes`, {
        method: 'POST',
        body: formData
      })
      
      if (!res.ok) {
        const errorText = await res.text()
        throw new Error(`Error ${res.status}: ${errorText}`)
      }

      const result = await res.json()
      console.log('Nota agregada exitosamente:', result)
      setNewNote('')
      setNewNoteFiles([])
      
      // Recargar notas inmediatamente después de publicar
      await loadTicketData()
    } catch (err) {
      console.error('Error agregando nota:', err)
      alert(`Error al agregar nota: ${err.message}`)
    }
  }

  const handleNoteFilesChange = (event) => {
    const selected = Array.from(event.target.files || [])

    if (selected.length > 4) {
      alert('Solo puedes seleccionar hasta 4 archivos')
      event.target.value = ''
      return
    }

    const invalid = selected.find((file) => {
      const mime = (file.type || '').toLowerCase()
      const name = (file.name || '').toLowerCase()
      const isImage = mime.startsWith('image/') || /\.(jpg|jpeg|png|webp)$/i.test(name)
      const isPdf = mime === 'application/pdf' || /\.pdf$/i.test(name)
      return !(isImage || isPdf)
    })

    if (invalid) {
      alert('Solo se permiten imágenes o PDF')
      event.target.value = ''
      return
    }

    setNewNoteFiles(selected)
  }

  const resolveAttachmentUrl = (url) => {
    if (!url) return ''
    if (url.startsWith('http')) return url
    const base = (import.meta.env.VITE_API_BASE || '').replace(/\/$/, '')
    return base ? `${base}${url}` : url
  }

  const handleDownloadFile = (file) => {
    const maybeUrl = file?.url || file?.path || ''
    const fileName = file?.filename || file?.name || ''

    let downloadPath = ''
    if (maybeUrl.startsWith('http')) {
      downloadPath = maybeUrl
    } else if (maybeUrl.startsWith('/api/uploads/')) {
      downloadPath = maybeUrl
    } else if (maybeUrl.startsWith('/uploads/')) {
      downloadPath = maybeUrl
    } else {
      const last = (maybeUrl.split('/').pop() || fileName || '').trim()
      if (!last) {
        alert('⚠️ El archivo no tiene URL disponible. Puede ser un nombre de archivo sin referencia.')
        return
      }
      const safeName = encodeURIComponent(last)
      downloadPath = `/uploads/${safeName}`
    }

    let downloadUrl = downloadPath
    if (!downloadUrl.startsWith('http')) {
      const base = (import.meta.env.VITE_API_BASE || '').replace(/\/$/, '')
      if (base) {
        downloadUrl = `${base}${downloadUrl}`
      }
    }

    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = file?.name || 'archivo'
    link.target = '_blank'
    link.rel = 'noopener noreferrer'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  async function handleStatusChange() {
    try {
      const statusMap = {
        'pending': 'pending',
        'in_production': 'in_production',
        'En Producción': 'in_production',
        'ready': 'ready',
        'Lista para Entregar': 'ready',
        'delivered': 'delivered',
        'Entregada': 'delivered',
        'warranty': 'warranty',
        'Garantía': 'warranty'
      }

      const normalized = normalizeStatus(newStatus)
      const mappedStatus = statusMap[normalized] || normalized

      const res = await apiFetch(`/api/requests/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: mappedStatus })
      })
      
      if (res.ok) {
        // Crear nota automática del cambio de estado
        const statusLabel = {
          'pending': 'Pendiente',
          'in_production': 'En Producción',
          'ready': 'Lista para Entregar',
          'delivered': 'Entregada',
          'warranty': 'Garantía'
        }
        
        const noteText = `${currentUser?.name || 'Usuario'} actualizó estado a "${statusLabel[mappedStatus] || mappedStatus}"`
        
        // Enviar nota automática
        await apiFetch(`/api/requests/${id}/workshop-notes`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            comment: noteText,
            isSystemNote: true
          })
        }).catch(err => console.warn('Error creating status change note:', err))
        
        await loadTicketData()
        alert('Estado actualizado')
      } else {
        throw new Error('Error al actualizar estado')
      }
    } catch (err) {
      alert('Error actualizando estado')
      console.error(err)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <LucvanHeader 
          title="Producción"
          user={currentUser}
          onLogout={handleLogout}
          showBack={true}
          onBack={() => navigate('/production')}
        />
        <div className="flex justify-center items-center h-96">
          <p className="text-gray-500">Cargando ticket...</p>
        </div>
      </div>
    )
  }

  if (error || !ticket) {
    return (
      <div className="min-h-screen bg-gray-50">
        <LucvanHeader />
        <div className="flex justify-center items-center h-96">
          <p className="text-red-500">{error || 'Ticket no encontrado'}</p>
        </div>
      </div>
    )
  }

  const files = (() => {
    try {
      if (!ticket.files) return []
      if (Array.isArray(ticket.files)) return ticket.files
      const parsed = JSON.parse(ticket.files)
      return Array.isArray(parsed) ? parsed : []
    } catch (e) {
      console.warn('[ProductionTicketDetail] Error parsing files:', e, ticket.files)
      return []
    }
  })()

  const normalizedNewStatus = normalizeStatus(newStatus)
  const normalizedTicketStatus = normalizeStatus(ticket?.status)
  const showLabelButton = ['ready', 'delivered'].includes(normalizedNewStatus) || ['ready', 'delivered'].includes(normalizedTicketStatus)

  // Configuración para normalizar especificaciones y mostrar la referencia visual sin clicks
  const specConfig = [
    {
      keys: ['arcoTransverso', 'ArcoTransverso'],
      label: 'Arco Transverso (AT)',
      image: '/images/cunas/arco-transverso.webp'
    },
    {
      keys: ['barraMetatarsal', 'BarraMetatarsal'],
      label: 'Barra Metatarsal',
      image: '/images/cunas/barra-metatarsal.webp'
    },
    {
      keys: ['cunaCalcaneaInterna', 'CunaCalcaneaInterna'],
      label: 'Cuña Calcánea Interna',
      image: '/images/cunas/cuna-calcanea-interna.webp'
    },
    {
      keys: ['cunaCalcaneaExtrema', 'CunaCalcaneaExtrema'],
      label: 'Cuña Calcánea Externa',
      image: '/images/cunas/cuna-calcanea-extrema.webp'
    },
    {
      keys: ['cunaCalcaneaLarga', 'CunaCalcaneaLarga'],
      label: 'Cuña Calcánea Larga',
      image: '/images/cunas/cuna-calcanea-larga.webp'
    },
    {
      keys: ['elevacionAL', 'Elevacional', 'ElevacionAL'],
      label: 'Elevación AL',
      image: '/images/cunas/elevacion-al.webp'
    },
    {
      keys: ['alza', 'Alza'],
      label: 'Alza',
      image: '/images/cunas/Alza.webp'
    }
  ]

  const getSpecValue = (specs, keys) => {
    for (const k of keys) {
      if (specs && Object.prototype.hasOwnProperty.call(specs, k)) {
        return specs[k]
      }
    }
    return null
  }

  const isSpecActive = (value) => {
    if (!value && value !== 0) return false
    if (typeof value === 'boolean') return value
    if (typeof value === 'string') return value.trim() !== '' && value !== 'false'
    if (typeof value === 'number') return true
    if (typeof value === 'object') {
      return Object.values(value).some((v) => {
        if (!v && v !== 0) return false
        if (typeof v === 'boolean') return v
        if (typeof v === 'string') return v.trim() !== '' && v !== 'false'
        if (typeof v === 'number') return true
        if (typeof v === 'object') {
          return Object.values(v).some((inner) => inner && inner !== 'false')
        }
        return false
      })
    }
    return false
  }

  const renderFootSide = () => {
    if (!ticket.foot_side) return '-'
    const value = ticket.foot_side.toLowerCase()
    if (value.includes('izq')) return 'Izquierdo'
    if (value.includes('der')) return 'Derecho'
    if (value.includes('amb')) return 'Ambos'
    return ticket.foot_side
  }

  const renderSpecDetails = (value) => {
    if (!value) return null

    if (typeof value === 'boolean') {
      return value ? null : null
    }

    if (typeof value === 'string' || typeof value === 'number') {
      const val = String(value).toLowerCase()
      if (val === 'true' || val === 'false') return null
      return <span className="text-sm text-gray-800">{String(value)}</span>
    }

    // Handle new elevacionAL structure FIRST: {izqSelected: bool, izqMm: string, derSelected: bool, derMm: string}
    if (typeof value === 'object' && (value.izqSelected !== undefined || value.derSelected !== undefined)) {
      return (
        <div className="flex flex-col gap-1 text-sm text-gray-800">
          {value.izqSelected && value.izqMm && (
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-xs font-bold">Izq</span>
              <span>{value.izqMm}mm</span>
            </div>
          )}
          {value.derSelected && value.derMm && (
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 rounded bg-red-100 text-red-800 text-xs font-bold">Der</span>
              <span>{value.derMm}mm</span>
            </div>
          )}
        </div>
      )
    }

    if (typeof value === 'object') {
      const left = value.izq || value.left
      const right = value.der || value.right
      return (
        <div className="flex flex-col gap-1 text-sm text-gray-800">
          {left && (
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-xs font-bold">Izq</span>
              <span>{typeof left === 'object' ? `${left.tipo || ''} ${left.mm || ''}mm`.trim() : (String(left).toLowerCase() === 'true' ? '' : String(left))}</span>
            </div>
          )}
          {right && (
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 rounded bg-red-100 text-red-800 text-xs font-bold">Der</span>
              <span>{typeof right === 'object' ? `${right.tipo || ''} ${right.mm || ''}mm`.trim() : (String(right).toLowerCase() === 'true' ? '' : String(right))}</span>
            </div>
          )}
        </div>
      )
    }

    return null
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      <LucvanHeader 
        title="Producción"
        user={currentUser}
        onLogout={handleLogout}
        showBack={true}
        onBack={() => navigate('/production')}
      />
      
      <div className="p-6">
        {/* Modal */}
        <div className="bg-white rounded-xl max-w-7xl mx-auto overflow-hidden shadow-lg">
          {/* Header con ID y Estado */}
          <div className="sticky top-0 bg-gradient-to-r from-[#003C63] to-[#0066A4] text-white px-6 py-2 flex justify-between items-center z-10">
            <div className="flex items-center gap-2">
              <span className="bg-[#F5C400] text-[#003C63] text-sm font-bold px-2 py-0.5 rounded">#{ticket.consecutive_number || ticket.id}</span>
              <h2 className="text-lg font-bold">Detalles del Ticket</h2>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="px-2 py-1 border-2 border-[#F5C400] rounded text-[#003C63] font-semibold text-sm focus:ring-2 focus:ring-[#F5C400] focus:border-[#F5C400]"
              >
                {ticket.status !== 'warranty' && <option value="pending">Pendiente</option>}
                {ticket.status === 'warranty' && <option value="warranty">Garantía</option>}
                {ticket.status !== 'warranty' && <option value="in_production">En Producción</option>}
                <option value="ready">Lista para Entregar</option>
                <option value="delivered">Entregada</option>
              </select>
              <button
                onClick={handleStatusChange}
                className="bg-[#F5C400] text-[#003C63] px-3 py-1 rounded font-bold hover:bg-yellow-500 transition text-sm"
              >
                Guardar
              </button>
              {showLabelButton && (
                <button
                  onClick={() => setShowLabel(true)}
                  className="bg-orange-600 text-white px-3 py-1 rounded font-bold hover:bg-orange-700 transition text-sm flex items-center gap-1"
                >
                  <img src="/iconos/price-tag.png" alt="" className="w-4 h-4" />
                  Etiqueta
                </button>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
                        {/* Alerta de Garantía */}
                        {ticket.status === 'warranty' && (
                          <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded">
                            <div className="flex items-start gap-3">
                              <span className="text-2xl">🔧</span>
                              <div className="flex-1">
                                <h4 className="font-bold text-orange-800 mb-1">Solicitud en Garantía</h4>
                                <p className="text-sm text-orange-700 mb-2">
                                  <strong>Motivo:</strong> {ticket.warranty_reason || 'No especificado'}
                                </p>
                                {ticket.warranty_date && (
                                  <p className="text-xs text-orange-600">
                                    Fecha: {formatDateCR(ticket.warranty_date)}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

            {/* Información Básica - Más amplia */}
            <div className="bg-[#F4F6F8] rounded-lg p-4 space-y-3">
              <h3 className="font-bold text-[#003C63] text-base mb-3">Información de la Solicitud</h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-xs text-gray-500 font-semibold">Paciente</p>
                  <p className="font-semibold text-gray-900">{ticket.patient_name || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-semibold">Clínica</p>
                  <p className="font-semibold text-gray-900">{ticket.clinic_name || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-semibold">Doctor</p>
                  <p className="font-semibold text-gray-900">{ticket.doctor_name || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-semibold">Fecha Solicitud</p>
                  <p className="font-semibold text-gray-900">
                    {formatDateCR(ticket.created_at, false).split(',')[0]}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-semibold">Tipo de Plantilla</p>
                  <p className="font-semibold text-gray-900">{ticket.template_type || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-semibold">Pie a Trabajar</p>
                  <p className="font-semibold text-gray-900">{renderFootSide()}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-semibold">Talla de Calzado</p>
                  <p className="font-semibold text-gray-900">{ticket.shoe_size || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-semibold">Doble Forro</p>
                  <p className="font-semibold text-gray-900">
                    {ticket.double_lining === 'si' ? 'Sí' : ticket.double_lining === 'no' ? 'No' : '-'}
                  </p>
                </div>
              </div>
            </div>

            {/* Comentarios de la Clínica */}
            {ticket.comments && (
              <div className="bg-amber-50 rounded-lg p-4 border-l-4 border-amber-400">
                <h3 className="font-bold text-amber-900 mb-2 text-sm">📝 Comentarios de la Clínica</h3>
                <p className="text-gray-700 text-sm bg-white p-3 rounded">{ticket.comments}</p>
              </div>
            )}

            {/* Observaciones del doctor */}
            {ticket.observations && (
              <div className="bg-gray-100 rounded-lg p-4 border-l-4 border-gray-400">
                <h3 className="font-bold text-gray-800 mb-2 text-sm">Observaciones del Doctor</h3>
                <p className="text-gray-800 text-sm bg-white p-3 rounded">{ticket.observations}</p>
              </div>
            )}

            {/* Archivos adjuntos */}
            {files.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h3 className="font-bold text-[#003C63] text-sm mb-2">Archivos adjuntos</h3>
                <div className="space-y-2">
                  {files.map((file, idx) => (
                    <button
                      key={file.url || idx}
                      onClick={() => handleDownloadFile(file)}
                      className="w-full text-left flex items-center gap-2 text-sm text-blue-700 hover:text-blue-900 hover:bg-blue-50 p-2 rounded transition"
                      title={file.url ? 'Click para descargar' : 'URL no disponible'}
                    >
                      📎 {file.name || `Archivo ${idx + 1}`}
                      {file.size ? <span className="text-xs text-gray-500">({Math.round(file.size / 1024)} KB)</span> : null}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Especificaciones + referencias visuales (sin clicks) */}
            {ticket.specs && Object.keys(ticket.specs).length > 0 && (
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="font-bold text-[#003C63] text-base mb-3">Especificaciones solicitadas (con referencia visual)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {(() => {
                    const cards = specConfig.map((spec) => {
                      const value = getSpecValue(ticket.specs, spec.keys)
                      if (!isSpecActive(value)) return null
                      return (
                        <div key={spec.label} className="bg-white p-3 rounded border-2 border-blue-200 flex flex-col gap-2">
                          <div className="flex items-center gap-3">
                            <img
                              src={spec.image}
                              alt={spec.label}
                              className="w-16 h-16 object-contain rounded border border-gray-200"
                              onError={(e) => { e.target.style.display = 'none' }}
                            />
                            <div>
                              <p className="text-xs text-blue-600 font-bold uppercase">{spec.label}</p>
                              <p className="text-[11px] text-gray-500">Vista de referencia</p>
                            </div>
                          </div>
                          {renderSpecDetails(value) && (
                            <div className="pl-1">
                              {renderSpecDetails(value)}
                            </div>
                          )}
                        </div>
                      )
                    }).filter(Boolean)

                    if (!cards.length) {
                      return <p className="text-sm text-gray-500">Sin especificaciones solicitadas.</p>
                    }

                    return cards
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Notas - Estilo Facebook */}
        <div className="max-w-7xl mx-auto mt-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="font-bold text-[#003C63] text-lg mb-4">💬 Notas Internas del Taller</h3>

            {/* Input para nueva nota */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Escribe una nota interna sobre el progreso del trabajo..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#003C63] focus:border-transparent resize-none"
                rows="3"
              />
              <input
                type="file"
                accept="image/*,.pdf"
                multiple
                onChange={handleNoteFilesChange}
                className="mt-2 block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-3 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-[#E6F0F7] file:text-[#003C63] hover:file:bg-[#D7E8F4]"
              />
              {newNoteFiles.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  {newNoteFiles.length} archivo(s) adjunto(s): {newNoteFiles.map((file) => file.name).join(', ')}
                </p>
              )}
              <button
                onClick={handleAddNote}
                disabled={!newNote.trim()}
                className="mt-2 bg-[#003C63] text-white px-4 py-2 rounded hover:bg-[#005a8f] disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold text-sm transition"
              >
                💬 Publicar Nota
              </button>
            </div>

            {/* Lista de notas - Estilo Facebook */}
            <div className="space-y-4">
              {workshopNotes.length === 0 ? (
                <div className="text-gray-400 text-center py-8">
                  <p className="text-lg">💭</p>
                  <p>Sin notas aún. Sé el primero en comentar.</p>
                </div>
              ) : (
                workshopNotes.map((note, index) => (
                  <div key={note.id || index} className="pb-2">
                    {/* Nota del Sistema - Estilo WhatsApp */}
                    {note.is_system_note ? (
                      <div className="flex justify-center py-2">
                        <div className="bg-gray-200 text-gray-600 text-xs px-3 py-2 rounded-lg text-center max-w-xs">
                          <p className="font-medium text-gray-700">{note.comment}</p>
                          <p className="text-gray-500 text-[10px] mt-1">{formatDateCR(note.created_at, false)}</p>
                        </div>
                      </div>
                    ) : (
                      /* Nota de Usuario - Estilo WhatsApp compacto */
                      <div className="flex gap-2 items-start">
                        <div className="w-8 h-8 bg-[#003C63] text-white rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0">
                          {note.user_name?.charAt(0).toUpperCase() || '👤'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="bg-[#E7F3E7] rounded-lg px-3 py-2 shadow-sm">
                            <div className="flex items-baseline justify-between gap-2 mb-1">
                              <p className="font-semibold text-xs text-[#003C63]">{note.user_name || 'Anónimo'}</p>
                              <p className="text-[10px] text-gray-500 whitespace-nowrap">{formatDateCR(note.created_at, false)}</p>
                            </div>
                            <p className="text-sm text-gray-800 whitespace-pre-wrap break-words leading-relaxed">{note.comment}</p>
                            {Array.isArray(note.attachments) && note.attachments.length > 0 && (
                              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                                {note.attachments.map((file, fileIndex) => {
                                  const fileUrl = resolveAttachmentUrl(file.url)
                                  const isImage = (file.mimetype || '').startsWith('image/')
                                  return (
                                    <a
                                      key={file.id || `${note.id || index}-file-${fileIndex}`}
                                      href={fileUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="block"
                                    >
                                      {isImage ? (
                                        <img
                                          src={fileUrl}
                                          alt={file.name || 'Adjunto'}
                                          className="w-full h-20 object-cover rounded border border-gray-200"
                                        />
                                      ) : (
                                        <div className="h-20 rounded border border-gray-200 bg-white flex items-center justify-center text-xs text-[#003C63] font-semibold px-2 text-center">
                                          📄 {file.name || 'PDF'}
                                        </div>
                                      )}
                                    </a>
                                  )
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Etiqueta */}
      {showLabel && ticket && (
        <ProductLabel
          request={ticket}
          patient={{ name: ticket.patient_name }}
          onClose={() => setShowLabel(false)}
        />
      )}
    </div>
  )
}
